import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import { IconButton, Stack, TextField, Typography } from "@mui/material";
import { useEffect, useState } from "react";

import { marketplaceTypography as mt } from "../constants/marketplaceTypography";

interface MarketplaceQuantityControlProps {
  quantity: number;
  stockLimit?: number | null;
  disabled?: boolean;
  canIncrease?: boolean;
  onChange: (quantity: number) => void;
}

/** Controle de quantidade com input numérico editável. */
export function MarketplaceQuantityControl({
  quantity,
  stockLimit = null,
  disabled = false,
  canIncrease = true,
  onChange,
}: MarketplaceQuantityControlProps) {
  const [inputValue, setInputValue] = useState(String(quantity));

  useEffect(() => {
    setInputValue(String(quantity));
  }, [quantity]);

  const applyQuantity = (raw: string) => {
    const parsed = parseInt(raw, 10);
    if (Number.isNaN(parsed) || raw.trim() === "") {
      onChange(quantity > 0 ? quantity : 1);
      setInputValue(String(quantity > 0 ? quantity : 1));
      return;
    }
    onChange(parsed);
  };

  return (
    <Stack spacing={0.5}>
      <Stack direction="row" alignItems="center" spacing={0.5}>
        <IconButton
          size="small"
          aria-label="Diminuir quantidade"
          disabled={disabled || quantity <= 0}
          onClick={() => onChange(quantity - 1)}
          sx={{ border: "1px solid", borderColor: "divider" }}
        >
          <RemoveIcon fontSize="small" />
        </IconButton>
        <TextField
          value={inputValue}
          disabled={disabled}
          onChange={(e) => setInputValue(e.target.value.replace(/\D/g, ""))}
          onBlur={() => applyQuantity(inputValue)}
          onKeyDown={(e) => {
            if (e.key === "Enter") applyQuantity(inputValue);
          }}
          inputProps={{
            inputMode: "numeric",
            min: 0,
            max: stockLimit ?? undefined,
            style: { textAlign: "center", padding: "6px 4px" },
          }}
          sx={{
            width: 56,
            "& .MuiInputBase-root": { height: 36 },
            "& input": { ...mt.bodySecondary, fontWeight: 700 },
          }}
        />
        <IconButton
          size="small"
          aria-label="Aumentar quantidade"
          disabled={disabled || !canIncrease}
          onClick={() => onChange(quantity + 1)}
          sx={{ border: "1px solid", borderColor: "divider" }}
        >
          <AddIcon fontSize="small" />
        </IconButton>
      </Stack>
      {stockLimit !== null && stockLimit > 0 ? (
        <Typography sx={{ ...mt.bodySecondary, color: "text.secondary", fontSize: "0.8125rem" }}>
          Máx. {stockLimit} em estoque
        </Typography>
      ) : null}
    </Stack>
  );
}
